<template>
  <div class="resource-associator">

    <FileSelect outlined v-model="selectedResource" :options="resources" option-label="title" emit-value map-options
      label="Fichiers" @update:model-value="handlePrimaryResourceChange" dense />

    <template v-if="!associationsDisabled">
      <div class="associator-field">
        <p class="select-resource-title">Choisir les objets associés</p>
        <FileSelect
          outlined
          v-model="associatedResources"
          :options="resourcesFull"
          :option-value="opt => opt.resource_id || opt.id"
          option-label="title"
          map-options
          label="Objets"
          multiple
          dense
          use-chips
        />
      </div>
      <div class="associator-actions">
        <q-btn color="secondary" @click="associateResources">
          Associer
        </q-btn>
      </div>
    </template>
    <q-banner v-else class="quiz-info-banner" dense rounded>
      Le quiz s'ouvre depuis l'objet sélectionné. Aucun objet associé n'est nécessaire.
    </q-banner>
    
    <!-- Liste des ressources associées avec boutons pour changer l'ordre -->
    <div v-if="!associationsDisabled && associatedResources && associatedResources.length > 0" class="associated-resources-list">
      <p class="select-resource-title">Ordre des objets associés</p>

      <!-- Vue standard (comportement d'origine, inchangé) -->
      <q-list v-if="!pairingMode" bordered separator>
        <q-item v-for="(resource, index) in associatedResources" :key="resource.resource_id || resource.id">
          <q-item-section>
            {{ resource.title }}
          </q-item-section>
          <q-item-section side>
            <div class="action-icons">
              <q-btn flat icon="arrow_upward" @click="up(index)" :disable="index === 0"></q-btn>
              <q-btn flat icon="arrow_downward" @click="down(index)" :disable="index === associatedResources.length - 1"></q-btn>
            </div>
          </q-item-section>
        </q-item>
      </q-list>

      <!-- Vue "paires" (mode appairage Avant / Après) -->
      <div v-else class="pairs-view">
        <div
          v-for="group in pairedGroups"
          :key="'pair-' + group.pairIndex"
          class="pair-box"
          :class="{ 'pair-box--alt': group.pairIndex % 2 === 1, 'pair-box--incomplete': !group.second }"
        >
          <div class="pair-box__header">Paire {{ group.pairIndex + 1 }}</div>

          <div class="pair-row">
            <q-badge class="pair-badge" color="blue-grey-7" label="Avant" />
            <span class="pair-title">{{ group.first.resource.title }}</span>
            <div class="action-icons">
              <q-btn flat dense icon="arrow_upward" text-color="black" @click="up(group.first.flatIndex)" :disable="group.first.flatIndex === 0"></q-btn>
              <q-btn flat dense icon="arrow_downward" text-color="black" @click="down(group.first.flatIndex)" :disable="group.first.flatIndex === associatedResources.length - 1"></q-btn>
            </div>
          </div>

          <div v-if="group.second" class="pair-row">
            <q-badge class="pair-badge" color="teal-8" label="Après" />
            <span class="pair-title">{{ group.second.resource.title }}</span>
            <div class="action-icons">
              <q-btn flat dense icon="arrow_upward" text-color="black" @click="up(group.second.flatIndex)" :disable="group.second.flatIndex === 0"></q-btn>
              <q-btn flat dense icon="arrow_downward" text-color="black" @click="down(group.second.flatIndex)" :disable="group.second.flatIndex === associatedResources.length - 1"></q-btn>
            </div>
          </div>

          <div v-else class="pair-row pair-row--alone">
            <q-badge class="pair-badge" color="grey-6" label="Seul" />
            <span class="pair-title pair-title--muted">Affiché seul (pas de paire)</span>
          </div>
        </div>

        <q-banner v-if="oddAssociated" class="app-banner app-banner--warning q-mt-sm" dense rounded>
          <template v-slot:avatar>
            <q-icon name="warning" color="black" />
          </template>
          Nombre impair d'objets : le dernier s'affichera seul.
        </q-banner>
      </div>

      <div class="associator-actions">
        <q-btn color="secondary" @click="saveAllIndices">
          Enregistrer l'ordre
        </q-btn>
      </div>
    </div>
    
    <q-banner v-if="success" class="app-banner app-banner--success" dense rounded>
      Données enregistrées
    </q-banner>
    <q-banner v-if="errorBanner" class="app-banner app-banner--error" dense rounded>
      Erreur durant l'enregistrement des données
    </q-banner>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from "vuex";
import axios from 'axios';
import FileSelect from './FileSelect.vue';
export default {
  components: { FileSelect },
  props: {
    selectedPointId: {
      type: [Number, String],
      required: false,
      default: null
    },
    associationsDisabled: {
      type: Boolean,
      default: false
    },
    // Quand vrai, matérialise visuellement les paires "Avant / Après" dans la liste
    // des objets associés (utilisé par l'animation Avant/Après). Défaut : aucun changement.
    pairingMode: {
      type: Boolean,
      default: false
    }
  },
  expose: ['fetchResourcesFromPoint', 'associateResources'],
  setup(props, ctx) {
    const store = useStore();
    const resources = ref();
    const resourcesFull = ref([]);
    const selectedResource = ref(null);
    const associatedResources = ref();
    const selectedPoint = ref(props.selectedPointId);
    const model = ref(null);
    const resourcesFromPoints = ref([]);
    const options = ref([]);
    const mainResourceId = ref(null);
    const success = ref(false);
    const errorBanner = ref(false);
    const start = async () => {
      await fetchResourcesFromPoint();
      //await handleAssociatedResourceChange();
    }

    // Regroupe les objets associés 2 par 2, dans leur ordre courant, pour la
    // visualisation des paires Avant / Après (mode appairage). Se recalcule
    // automatiquement quand l'ordre change (up/down réassignent le tableau).
    const pairedGroups = computed(() => {
      const list = associatedResources.value || [];
      const groups = [];
      for (let i = 0; i < list.length; i += 2) {
        groups.push({
          pairIndex: groups.length,
          first: { resource: list[i], flatIndex: i },
          second: i + 1 < list.length
            ? { resource: list[i + 1], flatIndex: i + 1 }
            : null,
        });
      }
      return groups;
    });

    const oddAssociated = computed(() =>
      ((associatedResources.value || []).length % 2) === 1
    );



    const associateResources = async () => {
      try {
        console.log("Début de associateResources");
        
        // Vérifier si la ressource principale est sélectionnée
        if (!selectedResource.value) {
          console.error("Aucune ressource principale sélectionnée");
          errorBanner.value = true;
          setTimeout(() => {
            errorBanner.value = false;
          }, 1500);
          return false; // Indiquer l'échec
        }
        
        if (props.associationsDisabled) {
          console.log("Mode quiz : suppression des anciennes associations");
          associatedResources.value = [];
          await addResourceAssociation(selectedResource.value, []);
          ctx.emit("updateAssociatedResources", []);
          return true;
        }

        // Attribuer un index séquentiel à chaque ressource
        if (associatedResources.value && associatedResources.value.length > 0) {
          console.log("Attribution des indices aux ressources associées");
          // Pour les ressources nouvellement associées, attribuer un index séquentiel
          associatedResources.value = associatedResources.value.map((resource, index) => ({
            ...resource,
            index: index
          }));
        } else {
          console.log("Aucune ressource associée à enregistrer");
        }
        
        console.log("Appel à addResourceAssociation");
        await addResourceAssociation(selectedResource.value, associatedResources.value || []);
        
        console.log("Émission de l'événement updateAssociatedResources");
        ctx.emit("updateAssociatedResources", associatedResources.value);
        
        console.log("Appel à handleSelect");
        handleSelect();
        
        console.log("associateResources terminé avec succès");
        return true; // Indiquer le succès
      } catch (error) {
        console.error("Erreur dans associateResources:", error);
        errorBanner.value = true;
        setTimeout(() => {
          errorBanner.value = false;
        }, 1500);
        return false; // Indiquer l'échec
      }
    }

    const handleSelect = () => {
      // selectedPoint.value = model.value.point_id
      ctx.emit("updateSelectedPoint", selectedPoint.value);
      start();
    }

    const getPoints = async () => {
      try {
        let url = `${process.env.VUE_APP_BASEURL}/pointsFromMainResource/${mainResourceId.value}`;
        let response = await axios.get(url);
        var outputJSON = {};

        response.data.forEach(function (item) {
          if (
            !Object.prototype.hasOwnProperty.call(outputJSON, item.point_id)
          ) {
            outputJSON[item.point_id] = [];
          }

          outputJSON[item.point_id].push(item);
        });
        resourcesFromPoints.value = outputJSON;
        store.commit("SET_RESOURCESFROMPOINTS", resourcesFromPoints)
        var uniqueData = [];
        for (let item of Object.values(resourcesFromPoints.value)) {
          if (item[0].point_id && item[0].name) {
            let exists = uniqueData.find(
              (dataItem) => dataItem.point_id === item.point_id
            );
            if (!exists) {
              uniqueData.push({
                point_id: item[0].point_id,
                name: item[0].name,
              });
            }
          }
        }
        options.value = uniqueData;
      } catch (error) {
        console.log(error);
      }
    };


    const getMainResource = async () => {
      try {
        let url = `${process.env.VUE_APP_BASEURL}/mainresource/${store.state.exhibitId}`;
        let response = await axios.get(url);
        //Récupération de l'id de la page principale
        mainResourceId.value = response.data[0].main_resource_id;
      } catch (error) {
        console.log(error);
      }
    };

    const addResourceAssociation = async (resource, associatedResourcesArray) => {
      try {
        let url = `${process.env.VUE_APP_BASEURL}/resourceAssociation`;
        console.log(url)
        let body = {
          mainResourceId: resource.resource_id || resource.id,
          associatedResourcesIds: [],
          pointId: selectedPoint.value,
          resourceIndices: []
        };
        console.log("associatedResourcesArray: ",associatedResourcesArray)
        for (const element of associatedResourcesArray) {
          const resourceId = element.resource_id || element.id;
          body.associatedResourcesIds.push(resourceId);
          body.resourceIndices.push({
            resource_id: resourceId,
            index: element.index || 0
          });
        }
        console.log("body resource associator: ",body)
        await axios.post(url, body);
        success.value = true;
        setTimeout(() => {
          success.value = false;
         //handleAssociatedResourceChange();
        }, 1500);
      } catch (error) {
        console.error(error);
        errorBanner.value = true;
        setTimeout(() => {
          errorBanner.value = false;
        }, 1500);
      }
    };

    // Recharge la liste des associations de la ressource sélectionnée, SANS émettre
    // d'événement (utilisé par le watcher associationsDisabled pour éviter la boucle
    // emit -> getStyleFromPoint -> displaySelected écrasé par la valeur en base).
    const refreshAssociations = async () => {
      try {
        let url = `${process.env.VUE_APP_BASEURL}/associations/${selectedResource.value.resource_id}/${selectedPoint.value}`;
        let response = await axios.get(url);
        // Ne pas transformer les données, garder les objets complets
        let resources = response.data.map(element => ({
          ...element,
          resource_id: element.id || element.resource_id,
          // S'assurer que chaque ressource a un index, utiliser 0 comme valeur par défaut
          index: element.index !== undefined ? element.index : 0
        }));

        // Trier les ressources par leur index
        resources.sort((a, b) => {
          // Si l'index est le même, conserver l'ordre d'origine
          if (a.index === b.index) return 0;
          return a.index - b.index;
        });

        associatedResources.value = resources;
        console.log("Associated resources: ", associatedResources.value)
      } catch (error) {
        console.log(error)
      }
    }

    const handlePrimaryResourceChange = async () => {
      await refreshAssociations();
      ctx.emit("selectedResource", selectedResource.value);
    }

    const handleAssociatedResourceChange = async () => {
      try {
        //Récupération de  toutes les association du point
        let url = `${process.env.VUE_APP_BASEURL}/associationsFromPoint/${selectedPoint.value}`;
        let response = await axios.get(url);
        let associatedsResources = response.data;
        console.log("Associations: ", associatedsResources)
        resources.value = resourcesFull.value.filter(resource => !associatedsResources.includes(resource.id))
        console.log("Resources: ", resources.value)
      } catch (error) {
        console.log(error);
      }
    }

    watch(() => props.selectedPointId, newVal => {
      selectedPoint.value = newVal;
      console.log("La valeur sélectionnée a changé : " + newVal);
      handleSelect();
    });

    // Bascule quiz <-> autre : ajuste seulement l'état LOCAL, sans émettre.
    // (Émettre ici relançait reloadComponent + getStyleFromPoint, qui écrasait
    // le type d'animation choisi par la valeur en base -> impossible de quitter "Quiz".)
    watch(() => props.associationsDisabled, async (disabled) => {
      if (disabled) {
        associatedResources.value = [];
      } else if (selectedResource.value) {
        await refreshAssociations();
      }
    });

    const fetchResourcesFromPoint = async () => {
      try {
        // Sauvegarder les sélections actuelles avant de rafraîchir les données
        const currentSelectedResource = selectedResource.value;
        const currentAssociatedResources = associatedResources.value;
        
        let pointId = selectedPoint.value;
        let url = `${process.env.VUE_APP_BASEURL}/resourcesFromPoint/${pointId}`;
        let url2 = `${process.env.VUE_APP_BASEURL}/resources/${store.state.exhibitId}`;
        let response2 = await axios.get(url2);
        resourcesFull.value = response2.data;
        let response = await axios.get(url);
        resources.value = response.data
        console.log("Resources du point: ", resources.value)
        resources.value = resources.value.sort((a, b) => {
          const titleA = a.title ? a.title.toUpperCase() : "";
          const titleB = b.title ? b.title.toUpperCase() : "";
          if (titleA < titleB) return -1;
          if (titleA > titleB) return 1;
          return 0;
        });
        resourcesFull.value = resourcesFull.value.sort((a, b) => {
          const titleA = a.title ? a.title.toUpperCase() : "";
          const titleB = b.title ? b.title.toUpperCase() : "";
          if (titleA < titleB) return -1;
          if (titleA > titleB) return 1;
          return 0;
        });
        
        // Restaurer les sélections après le rafraîchissement
        if (currentSelectedResource) {
          // Trouver la ressource correspondante dans la nouvelle liste
          const updatedSelectedResource = resources.value.find(r => 
            r.resource_id === currentSelectedResource.resource_id || 
            r.id === currentSelectedResource.resource_id || 
            r.id === currentSelectedResource.id
          );
          
          if (updatedSelectedResource) {
            selectedResource.value = updatedSelectedResource;
            
            // Si nous avons restauré la ressource principale, restaurer aussi les associations
            if (currentAssociatedResources && currentAssociatedResources.length > 0) {
              // Conserver les associations
              associatedResources.value = currentAssociatedResources;
            }
            
            // Émettre l'événement selectedResource pour mettre à jour la prévisualisation
            ctx.emit("selectedResource", updatedSelectedResource);
            
            // Rafraîchir aussi la liste des associations pour cette ressource
            await handlePrimaryResourceChange();
          }
        }
      } catch (error) {
        console.log(error);
      }
    }

    // Fonction pour déplacer un objet vers le haut dans la liste
    const up = (index) => {
      if (index > 0 && associatedResources.value) {
        const temp = associatedResources.value[index - 1];
        associatedResources.value[index - 1] = associatedResources.value[index];
        associatedResources.value[index] = temp;
        
        // Forcer la réactivité en réassignant le tableau
        associatedResources.value = [...associatedResources.value];
      }
    };

    // Fonction pour déplacer un objet vers le bas dans la liste
    const down = (index) => {
      if (index < associatedResources.value.length - 1 && associatedResources.value) {
        const temp = associatedResources.value[index + 1];
        associatedResources.value[index + 1] = associatedResources.value[index];
        associatedResources.value[index] = temp;
        
        // Forcer la réactivité en réassignant le tableau
        associatedResources.value = [...associatedResources.value];
      }
    };

    // Fonction pour sauvegarder l'ordre des ressources associées
    const saveAllIndices = async () => {
      try {
        if (selectedResource.value && associatedResources.value) {
          // Préparer les données pour l'API
          const modifiedResources = associatedResources.value.map((resource, index) => ({
            ...resource,
            index: index,
            resource_id: resource.resource_id || resource.id
          }));
          
          // Appel à l'API pour sauvegarder l'ordre
          let url = `${process.env.VUE_APP_BASEURL}/resourceIndices`;
          await axios.put(url, { modifiedRows: modifiedResources });
          
          success.value = true;
          setTimeout(() => {
            success.value = false;
          }, 1500);
          
          // Émettre un événement pour informer le parent de la mise à jour
          ctx.emit("orderUpdated", associatedResources.value);
        }
      } catch (error) {
        console.log(error);
        errorBanner.value = true;
        setTimeout(() => {
          errorBanner.value = false;
        }, 1500);
      }
    };

    onMounted(async () => {
      await getMainResource();
      await getPoints();
      start();
    })

    return {
      resources,
      selectedResource,
      associatedResources,
      options,
      model,
      selectedPoint,
      success,
      resourcesFull,
      resourcesFromPoints,
      errorBanner,
      associateResources,
      start,
      addResourceAssociation,
      handleSelect,
      handlePrimaryResourceChange,
      fetchResourcesFromPoint,
      handleAssociatedResourceChange,
      up,
      down,
      saveAllIndices,
      pairedGroups,
      oddAssociated
    };
  },
};
</script>
<style scoped>
.resource-associator {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.associator-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Les boutons gardent leur largeur naturelle dans la colonne flex. */
.associator-actions {
  display: flex;
}

.select-resource-title,
.associated-resources-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: bold;
}

.banner {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 99999;
}

.associated-resources-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 10px;
  background-color: #fafafa;
}

.action-icons > .q-btn {
  margin-right: 2px;
}

/* --- Mode appairage (paires Avant / Après) --- */
.pairs-view {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pair-box {
  border: 2px solid #263238;
  border-radius: 0;
  background-color: #ffffff;
  padding: 8px 10px;
}

.pair-box--alt {
  background-color: #eceff1;
}

.pair-box--incomplete {
  border-style: dashed;
  border-color: #9e9e9e;
}

.pair-box__header {
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #455a64;
  margin-bottom: 6px;
}

.pair-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
}

.pair-badge {
  border-radius: 0;
  min-width: 64px;
  justify-content: center;
}

.pair-title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pair-title--muted {
  color: #9e9e9e;
  font-style: italic;
}

.app-banner--warning {
  background-color: #fff3e0;
  color: #000000;
}
</style>
